import { useCallback, useEffect, useMemo, useState } from 'react';

import { ChevronDownIcon, ChevronRightIcon, LockIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { CollapseHorizontalIcon, ExpandHorizontalIcon } from '@/components/atoms/Icons/ExpandIcon';
import { Button } from '@/components/ui/button';
import useBrains from '@/hooks/useBrains';
import { cn } from '@/lib/utils';
import {
  extractHeadingsFromMarkdown,
  HEADING_BLOCK_SELECTOR,
  HeadingItem,
} from '@/modules/aiAgents/helper';
import { selectIsBannerOpen } from '@/redux/banners/selectors';
import { selectBrainId } from '@/redux/session/selectors';

export type StickyNavigationSection = { id: string; label: string };

type HeadingNode = { text: string; level: 1 | 2; flatIndex: number; children: HeadingNode[] };

const buildHeadingTree = (headings: HeadingItem[]): HeadingNode[] => {
  const tree: HeadingNode[] = [];
  let currentH1: HeadingNode | null = null;

  headings.forEach((h, index) => {
    if (h.level === 1) {
      currentH1 = { text: h.text, level: 1, flatIndex: index, children: [] };
      tree.push(currentH1);
    } else if (currentH1) {
      currentH1.children.push({ text: h.text, level: 2, flatIndex: index, children: [] });
    } else {
      tree.push({ text: h.text, level: 2, flatIndex: index, children: [] });
    }
  });

  return tree;
};

type HeadingButtonProps = {
  text: string;
  isActive: boolean;
  onClick: () => void;
  sizeClass?: string;
  className?: string;
};

const HeadingButton = ({
  text,
  isActive,
  onClick,
  sizeClass = 'text-sm',
  className,
}: HeadingButtonProps) => (
  <button
    type="button"
    className={cn(
      'w-full rounded px-2 py-1 text-left text-muted-foreground transition-colors hover:bg-selected-soft hover:text-foreground',
      sizeClass,
      isActive && 'bg-selected-soft text-foreground',
      className
    )}
    onClick={onClick}
    title={text}
  >
    <span className="line-clamp-2">{text}</span>
  </button>
);

type StickyNavigationProps = {
  sections: StickyNavigationSection[];
  onSelect: (sectionId: string) => void;
  className?: string;
  liveMarkdownByField?: Record<string, string>;
  activeSectionId?: string | null;
  activeHeading?: { sectionId: string; headingIndex: number } | null;
  /**
   * Render without the viewport-fixed wrapper so the consumer can place the
   * nav in a layout (e.g. a grid cell with `position: sticky`). When false,
   * the legacy fixed-to-viewport positioning is applied.
   */
  inline?: boolean;
};

const readStoredRecord = (key: string): Record<string, boolean> => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
};

const readStoredBoolean = (key: string, fallback: boolean): boolean => {
  if (typeof window === 'undefined') {
    return fallback;
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) {
      return fallback;
    }
    return raw === 'true';
  } catch {
    return fallback;
  }
};

const STORAGE_KEYS = {
  sections: 'guidelines-nav:expanded-sections',
  h1s: 'guidelines-nav:expanded-h1s',
  collapsed: 'guidelines-nav:is-collapsed',
};

export const StickyNavigation = ({
  sections,
  onSelect,
  className,
  liveMarkdownByField,
  activeSectionId,
  activeHeading,
  inline = false,
}: StickyNavigationProps) => {
  const { t } = useTranslation();
  const isBannerOpen = useSelector(selectIsBannerOpen);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
    readStoredRecord(STORAGE_KEYS.sections)
  );
  const [expandedH1s, setExpandedH1s] = useState<Record<string, boolean>>(() =>
    readStoredRecord(STORAGE_KEYS.h1s)
  );
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() =>
    readStoredBoolean(STORAGE_KEYS.collapsed, false)
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEYS.sections, JSON.stringify(expandedSections));
    } catch {
      // storage may be unavailable; ignore
    }
  }, [expandedSections]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEYS.h1s, JSON.stringify(expandedH1s));
    } catch {
      // storage may be unavailable; ignore
    }
  }, [expandedH1s]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEYS.collapsed, String(isCollapsed));
    } catch {
      // storage may be unavailable; ignore
    }
  }, [isCollapsed]);

  const navigate = useNavigate();
  const location = useLocation();

  // Get brain data to access guidelines
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);

  // Extract heading items from all guideline sections, preferring live editor content
  const guidelineHeadingsBySection = useMemo(() => {
    const headingMap: Record<string, HeadingItem[]> = {};
    if (!brain?.guidelines && !liveMarkdownByField) {
      return headingMap;
    }

    const channelIds = new Set(['voice', 'email', 'chat']);
    const guidelines = brain?.guidelines;

    sections.forEach(({ id }) => {
      // Use live markdown if available, otherwise fall back to saved state
      const fallback = channelIds.has(id)
        ? guidelines?.channel_guidelines?.[id as 'voice' | 'email' | 'chat']
        : (guidelines as Record<string, unknown> | undefined)?.[id];
      const markdown = liveMarkdownByField?.[id] ?? fallback;
      if (typeof markdown === 'string') {
        const headings = extractHeadingsFromMarkdown(markdown);
        if (headings.length > 0) {
          headingMap[id] = headings;
        }
      }
    });

    return headingMap;
  }, [brain?.guidelines, liveMarkdownByField, sections]);

  // Pre-compute heading trees so buildHeadingTree isn't called inside .map() on every render
  const headingTreesBySection = useMemo(() => {
    const treeMap: Record<string, HeadingNode[]> = {};
    for (const [sectionId, headings] of Object.entries(guidelineHeadingsBySection)) {
      treeMap[sectionId] = buildHeadingTree(headings);
    }
    return treeMap;
  }, [guidelineHeadingsBySection]);

  // Initialize expanded sections when headings are first loaded
  useEffect(() => {
    setExpandedSections((prev) => {
      const updated = { ...prev };
      let hasChanges = false;

      Object.keys(guidelineHeadingsBySection).forEach((sectionId) => {
        if (updated[sectionId] === undefined) {
          updated[sectionId] = true;
          hasChanges = true;
        }
      });

      return hasChanges ? updated : prev;
    });
  }, [guidelineHeadingsBySection]);

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  const toggleH1 = useCallback((key: string) => {
    setExpandedH1s((prev) => ({ ...prev, [key]: !(prev[key] ?? true) }));
  }, []);

  // Scrolls the nav's own scrollport: `scrollIntoView` would also retarget the shared page
  // scroller and abort the smooth scroll a ToC click just started.
  const activeSectionRef = useCallback((node: HTMLButtonElement | null) => {
    const scrollport = node?.closest('nav');
    if (!node || !scrollport) {
      return;
    }

    const item = node.getBoundingClientRect();
    const port = scrollport.getBoundingClientRect();
    const delta =
      item.top < port.top
        ? item.top - port.top
        : item.bottom > port.bottom
          ? item.bottom - port.bottom
          : 0;

    if (delta !== 0) {
      scrollport.scrollBy({ top: delta, behavior: 'smooth' });
    }
  }, []);

  const handleNavigateToCustomInstructions = useCallback(() => {
    const customInstructionsSection = sections.find(
      (section) => section.id === 'custom_instructions'
    );
    if (!customInstructionsSection) {
      return;
    }

    // Expand the navigation if collapsed
    setIsCollapsed(false);

    // Add hash to trigger guideline creation (without navigation to avoid scroll)
    const targetUrl = `${location.pathname}${location.search}#create-new-guideline`;
    navigate(targetUrl, { replace: true });

    // Wait for the new heading to be created and scroll to it in one smooth motion
    const scrollToNewContent = (attempts = 0) => {
      if (attempts > 10) {
        return;
      } // Give up after 10 attempts

      const customInstructionsEditor = document.getElementById('guideline-custom-instructions');
      if (customInstructionsEditor) {
        // Find all heading blocks in the editor
        const headingBlocks = customInstructionsEditor.querySelectorAll(HEADING_BLOCK_SELECTOR);

        if (headingBlocks.length > 0) {
          // Scroll to the last heading (the newly created one)
          const lastHeading = headingBlocks[headingBlocks.length - 1];
          lastHeading.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }

      // If not found, try again after a short delay
      setTimeout(() => scrollToNewContent(attempts + 1), 100);
    };

    // Start scrolling attempts after initial delay
    setTimeout(() => scrollToNewContent(), 400);
  }, [sections, location, navigate]);

  const handleHeadingClick = useCallback((sectionId: string, flatIndex: number) => {
    const sectionEl = document.getElementById(`guideline-${sectionId.replace(/_/g, '-')}`);
    if (!sectionEl) {
      return;
    }

    // The nav lists H1/H2 only, so index against that same subset — the basis the scroll-spy uses.
    const el = Array.from(sectionEl.querySelectorAll(HEADING_BLOCK_SELECTOR)).filter(
      (block) => block.querySelector('h1, h2') && block.textContent?.trim()
    )[flatIndex];
    if (!el) {
      return;
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Delay until smooth scroll finishes, then animate via WAAPI so the
    // effect is visible when the heading is actually on screen.
    const headingEl = (el.querySelector('h1, h2, h3') ?? el) as HTMLElement;
    setTimeout(() => {
      const highlightColor = 'color-mix(in srgb, var(--selected) 15%, transparent)';

      const anim = headingEl.animate(
        [
          {
            backgroundColor: highlightColor,
            boxShadow: `-4px 0 0 4px ${highlightColor}`,
            borderRadius: '2px',
          },
          {
            backgroundColor: 'transparent',
            boxShadow: `-4px 0 0 4px transparent`,
            borderRadius: '2px',
          },
        ],
        { duration: 1500, easing: 'ease-out', fill: 'forwards' }
      );
      anim.onfinish = () => anim.cancel();
    }, 500);
  }, []);

  if (sections.length === 0) {
    return null;
  }

  const outerClasses = inline
    ? 'flex w-full flex-col items-start'
    : cn(
        'pointer-events-none fixed bottom-23 left-4 z-40 hidden w-52 items-start xl:flex xl:w-68 2xl:left-6',
        isBannerOpen ? 'top-39' : 'top-30'
      );

  return (
    <aside className={cn(outerClasses, className)}>
      <div className="pointer-events-auto flex max-h-full w-full flex-col overflow-hidden rounded-lg border border-transparent bg-transparent transition-colors duration-200 hover:border-border hover:bg-background">
        {/* Header */}
        <div className="flex min-h-12 shrink-0 items-center justify-between px-2 py-2">
          <Button
            size="sm"
            variant="ghost-neutral"
            className="h-7"
            onClick={() => setIsCollapsed((prev) => !prev)}
            aria-label={
              isCollapsed
                ? t('common.expand', 'Expand panel')
                : t('common.collapse', 'Collapse panel')
            }
          >
            <span className="text-sm leading-none font-semibold">
              {t('common.table_of_contents')}
            </span>

            {isCollapsed ? <ExpandHorizontalIcon /> : <CollapseHorizontalIcon />}
          </Button>

          {sections.some((section) => section.id === 'custom_instructions') && (
            <Button
              size="icon-sm"
              aria-label={t('common.add_guideline')}
              onClick={handleNavigateToCustomInstructions}
            >
              <PlusIcon size={16} />
            </Button>
          )}
        </div>

        {/* Navigation */}
        {!isCollapsed && (
          // pl-2.5 (10px) optically aligns section items with the header label
          <nav className="overflow-y-auto py-2 pr-2 pl-2.5">
            {sections.map((section) => {
              const headingTree = headingTreesBySection[section.id] || [];
              const hasHeadings = headingTree.length > 0;
              const isExpanded = expandedSections[section.id] ?? true;
              const isActive = activeSectionId === section.id;

              return (
                <div key={section.id} className="mb-1">
                  {/* Section Header */}
                  <div className="flex items-center">
                    {hasHeadings ? (
                      <button
                        type="button"
                        className="mr-1 flex h-6 w-6 items-center justify-center rounded hover:bg-selected-soft"
                        onClick={() => toggleSection(section.id)}
                        aria-label={isExpanded ? t('common.collapse') : t('common.expand')}
                      >
                        {isExpanded ? (
                          <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                    ) : (
                      <div className="mr-1 flex h-6 w-6 items-center justify-center">
                        <LockIcon className="h-3 w-3 text-muted-foreground" />
                      </div>
                    )}
                    <button
                      type="button"
                      ref={isActive ? activeSectionRef : undefined}
                      className={cn(
                        'flex-1 rounded px-2 py-1.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-selected-soft hover:text-foreground',
                        isActive && 'bg-selected-soft'
                      )}
                      onClick={() => onSelect(section.id)}
                    >
                      {section.label}
                    </button>
                  </div>

                  {/* Sub-headings with H1/H2 hierarchy */}
                  {hasHeadings && isExpanded && (
                    <div className="mt-1 ml-3 space-y-0.5 border-l border-border pl-4">
                      {headingTree.map((node) => {
                        if (node.level === 1) {
                          const h1Key = `${section.id}-h${node.flatIndex}`;
                          const isH1Expanded = expandedH1s[h1Key] ?? true;

                          return (
                            <div key={h1Key}>
                              <div className="flex items-center">
                                {node.children.length > 0 && (
                                  <button
                                    type="button"
                                    className="mr-0.5 flex h-5 w-5 items-center justify-center rounded hover:bg-selected-soft"
                                    onClick={() => toggleH1(h1Key)}
                                    aria-label={
                                      isH1Expanded ? t('common.collapse') : t('common.expand')
                                    }
                                  >
                                    {isH1Expanded ? (
                                      <ChevronDownIcon className="h-3 w-3 text-muted-foreground" />
                                    ) : (
                                      <ChevronRightIcon className="h-3 w-3 text-muted-foreground" />
                                    )}
                                  </button>
                                )}
                                <HeadingButton
                                  text={node.text}
                                  isActive={
                                    activeHeading?.sectionId === section.id &&
                                    activeHeading?.headingIndex === node.flatIndex
                                  }
                                  onClick={() => handleHeadingClick(section.id, node.flatIndex)}
                                  sizeClass="text-sm font-medium"
                                  className={!node.children.length ? 'ml-5' : undefined}
                                />
                              </div>

                              {/* H2 children nested under H1 */}
                              {isH1Expanded && node.children.length > 0 && (
                                <div className="ml-5 space-y-0.5 border-l border-border/50 pl-3">
                                  {node.children.map((child) => (
                                    <HeadingButton
                                      key={`${section.id}-h${child.flatIndex}`}
                                      text={child.text}
                                      isActive={
                                        activeHeading?.sectionId === section.id &&
                                        activeHeading?.headingIndex === child.flatIndex
                                      }
                                      onClick={() =>
                                        handleHeadingClick(section.id, child.flatIndex)
                                      }
                                      sizeClass="text-xs"
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        }

                        // Orphan H2 at top level (before any H1 in this section)
                        return (
                          <HeadingButton
                            key={`${section.id}-h${node.flatIndex}`}
                            text={node.text}
                            isActive={
                              activeHeading?.sectionId === section.id &&
                              activeHeading?.headingIndex === node.flatIndex
                            }
                            onClick={() => handleHeadingClick(section.id, node.flatIndex)}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        )}
      </div>
    </aside>
  );
};
